import { useCallback, useEffect, useMemo, useState } from "react";
import { api, equivalencesApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, Loader2, Layers, Boxes } from "lucide-react";
import { toast } from "sonner";

/**
 * Page Paramètres → Équivalences produits.
 * Permet de créer des groupes d'équivalence regroupant plusieurs produits
 * de fournisseurs différents qui partagent la même unité de référence.
 */
export default function EquivalencesPage() {
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | group object

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [g, p] = await Promise.all([
        equivalencesApi.listGroups(),
        api.getAllProducts(),
      ]);
      setGroups(g.data || []);
      setProducts(p.data || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (group) => {
    if (!window.confirm(`Supprimer le groupe « ${group.name} » ?`)) return;
    try {
      await equivalencesApi.deleteGroup(group.id);
      toast.success("Groupe supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <div className="space-y-5" data-testid="equivalences-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6 text-blue-700" />
            Équivalences produits
          </h1>
          <p className="text-sm text-muted-foreground">
            Regroupez des produits de fournisseurs différents qui peuvent se substituer entre eux
            (même unité, mêmes propriétés). Ils pourront ensuite être utilisés sur les commandes
            avec un cumul automatique des stocks disponibles.
          </p>
        </div>
        <Button onClick={() => setEditing("new")} data-testid="equiv-new-group-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau groupe
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Aucun groupe créé. Cliquez sur « Nouveau groupe » pour commencer.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((g) => (
            <Card key={g.id} data-testid={`equiv-card-${g.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="truncate">{g.name}</span>
                  <Badge variant="secondary" className="text-xs">{g.reference_unit}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-xs text-muted-foreground">{(g.products || []).length} produit(s)</div>
                <ul className="text-xs space-y-0.5 max-h-32 overflow-auto pr-1">
                  {(g.products || []).map((p) => (
                    <li key={p.id} className="flex items-center gap-1 truncate">
                      <Boxes className="h-3 w-3 text-slate-400 shrink-0" />
                      <span className="truncate">{p.name}</span>
                      <span className="text-slate-400 shrink-0">· {p.supplier_name || "—"}</span>
                    </li>
                  ))}
                </ul>
                {g.notes && (
                  <p className="text-xs italic text-slate-600 border-t pt-1">{g.notes}</p>
                )}
                <div className="flex gap-1 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(g)}
                    data-testid={`equiv-edit-${g.id}`}
                    className="flex-1"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Modifier
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(g)}
                    data-testid={`equiv-delete-${g.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <EquivalenceGroupDialog
          group={editing === "new" ? null : editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EquivalenceGroupDialog({ group, products, onClose, onSaved }) {
  const isNew = !group;
  const [name, setName] = useState(group?.name || "");
  const [refUnit, setRefUnit] = useState(group?.reference_unit || "m2");
  const [selectedIds, setSelectedIds] = useState(
    new Set((group?.product_ids) || (group?.products || []).map((p) => p.id) || []),
  );
  const [notes, setNotes] = useState(group?.notes || "");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Unique units from existing products
  const allUnits = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.reference_unit) set.add(p.reference_unit); });
    return ["m2", "kg", "l", "m", "u"].concat([...set].filter((u) => !["m2", "kg", "l", "m", "u"].includes(u)));
  }, [products]);

  // Filter products by current refUnit + search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => (p.reference_unit || "") === refUnit)
      .filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.supplier_name || "").toLowerCase().includes(q));
  }, [products, refUnit, search]);

  const toggle = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Nom requis");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        product_ids: [...selectedIds],
        notes: notes.trim() || null,
      };
      if (isNew) {
        await equivalencesApi.createGroup({ ...payload, reference_unit: refUnit });
        toast.success("Groupe créé");
      } else {
        await equivalencesApi.updateGroup(group.id, payload);
        toast.success("Groupe modifié");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="equiv-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nouveau groupe d'équivalence" : `Modifier — ${group.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 overflow-auto px-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nom du groupe</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Polystyrène graphité 120mm"
                data-testid="equiv-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Unité de référence</Label>
              <Select
                value={refUnit}
                onValueChange={(v) => { setRefUnit(v); setSelectedIds(new Set()); }}
                disabled={!isNew}
              >
                <SelectTrigger data-testid="equiv-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allUnits.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isNew && (
                <p className="text-[10px] text-muted-foreground mt-1">L'unité ne peut pas être modifiée après création.</p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optionnel)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Précisions internes (compatibilités, contraintes…)"
              data-testid="equiv-notes-input"
            />
          </div>
          <div>
            <Label className="text-xs">
              Produits du groupe ({selectedIds.size} sélectionné{selectedIds.size > 1 ? "s" : ""})
            </Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit…"
              className="mb-2"
              data-testid="equiv-product-search"
            />
            <div className="border rounded-md max-h-[300px] overflow-auto">
              {filtered.length === 0 ? (
                <p className="p-3 text-xs text-muted-foreground italic">
                  Aucun produit avec l'unité « {refUnit} ».
                </p>
              ) : (
                <ul className="divide-y">
                  {filtered.map((p) => {
                    const checked = selectedIds.has(p.id);
                    return (
                      <li
                        key={p.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggle(p.id)}
                        data-testid={`equiv-product-${p.id}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggle(p.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {p.supplier_name || "—"} · {p.reference || ""}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="equiv-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {isNew ? "Créer" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
